import { Loader } from "lucide-react";
import { FC, useCallback } from "react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useDeleteCustomVerificationTypeMutation } from "@/data/mutations";
import { useDialogState } from "@/hooks/use-dialog-state";

type Props = {
  verificationTypeName: string;
  verificationTypeId: number;
};

export const DeleteVerificationTypeDialog: FC<Props> = ({
  verificationTypeName,
  verificationTypeId,
}) => {
  const { dialog, closeDialog } = useDialogState();
  const deleteVerificationTypeMutation =
    useDeleteCustomVerificationTypeMutation();

  const handleSubmit = useCallback(() => {
    deleteVerificationTypeMutation.mutate(verificationTypeId);
  }, [deleteVerificationTypeMutation, verificationTypeId]);

  return (
    <AlertDialog open={dialog?.name === "delete-verification-type"}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {verificationTypeName}</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the{" "}
            <b className="text-black">{verificationTypeName}</b> verification
            type? This action is irreversible.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={closeDialog}
            disabled={deleteVerificationTypeMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={deleteVerificationTypeMutation.isPending}
          >
            {deleteVerificationTypeMutation.isPending ? (
              <Loader className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
